import React, { useEffect, useState } from "react";
import PageTopBar from "../../../../components/common/PageTopBar";
import { toolbarButtonSx, toolbarFieldsSx } from "../../../../components/common/toolbarStyles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EastIcon from "@mui/icons-material/East";
import { useGradePeriods } from "../../hooks/useGradePeriods";
import { DayOfWeek, WORKING_DAYS } from "../../types";
import { UnsavedChangesBar } from "../../../settings/components/UnsavedChangesBar";
import { useNotify } from "../../../../context/NotificationContext";

const DAY_LABELS: Record<DayOfWeek, string> = {
  MONDAY: "Mon",
  TUESDAY: "Tue",
  WEDNESDAY: "Wed",
  THURSDAY: "Thu",
  FRIDAY: "Fri",
  SATURDAY: "Sat",
};

const headCellSx = { fontWeight: 600, py: 1, bgcolor: "grey.50" };

/**
 * Which periods of the bell schedule each grade uses on each working day (e.g. a shorter Saturday).
 * One grade at a time; changes to several grades are saved together.
 */
const GradePeriodsUI: React.FC<{ tabs?: React.ReactNode }> = ({ tabs }) => {
  const notify = useNotify();
  const { grades, periods, draft, changedCodes, loading, saving, error, actions } = useGradePeriods();
  const [gradeCode, setGradeCode] = useState<string>("");
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTargets, setCopyTargets] = useState<string[]>([]);

  useEffect(() => {
    if (!gradeCode && grades.length > 0) setGradeCode(grades[0].code);
  }, [grades, gradeCode]);

  // warn before closing/reloading the tab with unsaved changes
  useEffect(() => {
    if (changedCodes.length === 0) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [changedCodes.length]);

  const days = draft[gradeCode] ?? {};
  const grade = grades.find((g) => g.code === gradeCode);
  const ticked = (day: DayOfWeek, periodId: number) => (days[day] ?? []).includes(periodId);
  const countFor = (day: DayOfWeek) => periods.filter((p) => !p.isBreak && ticked(day, p.id!)).length;

  const handleSave = async () => {
    const failed = await actions.saveAll();
    if (failed === 0) notify("Grade periods saved.", "success");
  };

  const openCopy = () => {
    setCopyTargets([]);
    setCopyOpen(true);
  };
  const applyCopy = () => {
    actions.copyToGrades(gradeCode, copyTargets);
    setCopyOpen(false);
    notify(`Copied ${grade?.name ?? ""}'s periods to ${copyTargets.length} grade(s). Save to keep them.`, "info");
  };

  const gradePicker = (
    <FormControl size="small" sx={{ ...toolbarFieldsSx, minWidth: 180 }} disabled={loading || grades.length === 0}>
      <InputLabel id="grade-periods-grade">Grade</InputLabel>
      <Select labelId="grade-periods-grade" label="Grade" value={grades.length ? gradeCode : ""} onChange={(event) => setGradeCode(event.target.value)}>
        {grades.map((g) => (
          <MenuItem key={g.code} value={g.code}>
            {g.name}
            {changedCodes.includes(g.code) && (
              <Typography component="span" variant="caption" color="warning.main" ml={1}>
                ● unsaved
              </Typography>
            )}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (periods.length === 0) {
      return (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No bell schedule yet. Add the school's periods in the <b>Bell Schedule</b> tab first.
        </Alert>
      );
    }
    if (grades.length === 0) {
      return (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No grades yet. Add grades in the <b>Grades</b> tab first.
        </Alert>
      );
    }

    return (
      <>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }} alignItems={{ sm: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Tick the periods <b>{grade?.name}</b> has on each day. Click a day or a period to tick the whole column or row.
          </Typography>
          <Button size="small" sx={{ ...toolbarButtonSx, textTransform: "none" }} startIcon={<EastIcon />} onClick={() => actions.copyMondayToAll(gradeCode)} disabled={saving}>
            Copy Monday → all days
          </Button>
          <Button size="small" sx={{ ...toolbarButtonSx, textTransform: "none" }} startIcon={<ContentCopyIcon />} onClick={openCopy} disabled={saving || grades.length < 2}>
            Copy to grades…
          </Button>
        </Stack>

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Period</TableCell>
                {WORKING_DAYS.map((day) => (
                  <TableCell key={day} align="center" sx={{ ...headCellSx, px: 0.5, minWidth: 64 }}>
                    <Tooltip title={`Tick or clear every period on ${DAY_LABELS[day]}`}>
                      <Button size="small" onClick={() => actions.toggleDay(gradeCode, day)} disabled={saving} sx={{ minWidth: 0, fontWeight: 600, textTransform: "none", color: "text.primary" }}>
                        {DAY_LABELS[day]}
                      </Button>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.map((period) => (
                <TableRow key={period.id} hover sx={{ bgcolor: period.isBreak ? "grey.50" : undefined, "&:last-child td": { border: 0 } }}>
                  <TableCell sx={{ py: 0.5 }}>
                    <Tooltip title="Tick or clear this period on every day" placement="right">
                      <Box
                        component="button"
                        type="button"
                        onClick={() => actions.togglePeriod(gradeCode, period.id!)}
                        disabled={saving}
                        sx={{ all: "unset", cursor: "pointer", display: "block", borderRadius: 1, px: 0.5, "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.06) } }}
                      >
                        <Typography variant="body2" fontWeight={period.isBreak ? 400 : 600} fontStyle={period.isBreak ? "italic" : undefined} color={period.isBreak ? "text.secondary" : "text.primary"}>
                          {period.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {period.startTime}–{period.endTime}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  {WORKING_DAYS.map((day) => (
                    <TableCell key={day} align="center" sx={{ py: 0, px: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={ticked(day, period.id!)}
                        onChange={() => actions.toggle(gradeCode, day, period.id!)}
                        disabled={saving}
                        sx={period.isBreak ? { color: "grey.400" } : undefined}
                        inputProps={{ "aria-label": `${grade?.name} ${DAY_LABELS[day]} ${period.name}` }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ ...headCellSx, color: "text.secondary", fontWeight: 500 }}>Teaching periods</TableCell>
                {WORKING_DAYS.map((day) => (
                  <TableCell key={day} align="center" sx={{ ...headCellSx, color: countFor(day) ? "text.primary" : "text.disabled" }}>
                    {countFor(day) || "Off"}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          A day with no periods ticked is a holiday for this grade.
        </Typography>
      </>
    );
  };

  return (
    <Box>
      <PageTopBar left={tabs ?? "Grade Periods"} right={gradePicker} />

      {error && changedCodes.length === 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {renderBody()}

      {/* last child of the page box, so it sticks to the bottom of the screen while scrolling */}
      <Box sx={{ height: 16 }} />
      <UnsavedChangesBar open={changedCodes.length > 0} saving={saving} error={error} onDiscard={actions.discard} onSave={handleSave} />

      <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600 }}>Copy {grade?.name}'s periods</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The selected grades get the same periods on every day. Their current ticks are replaced.
          </Typography>
          <Stack>
            {grades
              .filter((g) => g.code !== gradeCode)
              .map((g) => (
                <FormControlLabel
                  key={g.code}
                  label={g.name}
                  control={
                    <Checkbox
                      size="small"
                      checked={copyTargets.includes(g.code)}
                      onChange={() =>
                        setCopyTargets((prev) => (prev.includes(g.code) ? prev.filter((c) => c !== g.code) : [...prev, g.code]))
                      }
                    />
                  }
                />
              ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={applyCopy} disabled={copyTargets.length === 0} sx={{ textTransform: "none", boxShadow: "none" }}>
            Copy to {copyTargets.length || ""} grade{copyTargets.length === 1 ? "" : "s"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GradePeriodsUI;
